import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../../../auth/AuthContext';
import { connectXero } from '../../../../utils/xeroConnection';
import { xeroNeedsReconnect, xeroHealthWarning, xeroRateLimited } from '../../../../utils/xeroStatus';
import { doc } from 'firebase/firestore';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import { db } from '../../../../firebase-client';

function asDate(value) {
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

export default function FinanceIntegrationsModal({
  isOpen,
  onClose,
  onOpenXero,
  teamId,
}) {
  const { user } = useAuth();
  const [reconnecting, setReconnecting] = useState(false);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !teamId) return undefined;
    setLoading(true);
    setError('');

    const unsub = safeOnSnapshot(
      doc(db, 'teams', teamId, 'integrations', 'xero'),
      (snap) => {
        setConfig(snap.exists() ? (snap.data() || {}) : null);
        setLoading(false);
      },
      (snapshotError) => {
        setError(snapshotError?.message || 'Unable to load integrations.');
        setLoading(false);
      }
    );

    return () => unsub();
  }, [isOpen, teamId]);

  const handleReconnect = async () => {
    setReconnecting(true);
    setError('');
    try {
      await connectXero({ user, teamId });
    } catch (err) {
      setError(err?.message || 'Unable to reconnect Xero.');
    } finally {
      setReconnecting(false);
    }
  };

  if (!isOpen) return null;

  const lastSyncAt = asDate(config?.lastSyncAt);
  const healthWarning = xeroHealthWarning(config);
  const needsReconnect = xeroNeedsReconnect(config);
  const rateLimited = xeroRateLimited(config) && !needsReconnect && !config?.queueSummary?.failed;
  const xeroStatus = needsReconnect ? 'Reconnection required' : rateLimited && !config?.queueSummary?.failed ? 'Waiting for Xero' : healthWarning ? 'Needs attention' : config?.connected
    ? `Connected${config?.tenantName ? ` to ${config.tenantName}` : ''}`
    : 'Not connected';

  return ReactDOM.createPortal(
    <div className="modal-backdrop">
      <div className="modal-content" role="dialog" aria-modal="true" aria-label="Finance integrations" style={{ maxWidth: 760 }}>
        <div className="card-header-with-icon" style={{ marginBottom: 12 }}>
          <h2 className="modal-title" style={{ margin: 0 }}>Finance Integrations</h2>
          <button type="button" className="edit-btn" onClick={onClose} aria-label="Close integrations">
            <span aria-hidden="true">x</span>
          </button>
        </div>

        <p className="text-muted" style={{ marginTop: 0 }}>
          Connect accounting tools here. Member Nest can mirror club finance activity into external systems without changing how payments are taken in the app.
        </p>

        {loading ? <p className="text-muted">Loading integrations…</p> : (
          <>
            {error ? <p className="text-error" role="alert">{error}</p> : null}

            <div style={{
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: 14,
              padding: 16,
              marginBottom: 16,
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
                alignItems: 'flex-start',
                flexWrap: 'wrap',
              }}>
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      display: 'grid',
                      placeItems: 'center',
                      background: '#e8fbf2',
                      color: '#127a46',
                      fontWeight: 700,
                    }}>
                      X
                    </div>
                    <div>
                      <div style={{ fontWeight: 700 }}>Xero</div>
                      <div className="text-muted">Mirror the club Stripe balance into Xero.</div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  {needsReconnect ? (
                    <button type="button" className="btn btn--primary" onClick={handleReconnect} disabled={reconnecting}>
                      {reconnecting ? 'Reconnecting…' : 'Reconnect Xero'}
                    </button>
                  ) : null}
                  <button type="button" className={needsReconnect ? 'btn btn--secondary' : 'btn btn--primary'} onClick={onOpenXero} disabled={reconnecting}>
                    {config?.connected ? 'Manage Xero' : 'Set up Xero'}
                  </button>
                </div>
              </div>
              <div style={{ marginTop: 12, overflowWrap: 'anywhere' }}>
                <div style={{ marginBottom: 6 }}>
                  <strong>Status:</strong> {xeroStatus}
                </div>
                <div className="text-muted" style={{ marginBottom: 6 }}>
                  Sync records money received into Stripe and payouts leaving Stripe.
                </div>
                <div className="text-muted">
                  {lastSyncAt ? `Last sync: ${lastSyncAt.toLocaleString()}` : 'No sync has run yet.'}
                </div>
                {healthWarning ? <p className={rateLimited ? "text-muted" : "text-error"} role={rateLimited ? "status" : "alert"}>{healthWarning}</p> : null}
                {config?.lastSyncError ? (
                  <div className="text-error" style={{ marginTop: 8 }}>
                    Latest error: {config.lastSyncError}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="text-muted" style={{ marginBottom: 16 }}>
              More finance integrations are planned, let us know what you need.
            </div>

            <div className="form-actions" style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn--secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
